import { Directive, HostBinding, input, Input, output, OutputEmitterRef, Signal } from '@angular/core';
import { BooleanSignal, StringSignal } from '@studiohyperdrive/ngx-utils';

import { CypressTagsPaths } from '@vlaio/cypress/shared';
import { BrowserService } from '@vlaio/shared/core';

import { ButtonVariants, ButtonVariantsType } from './button.enums';

@Directive()
export class VlaioButtonAbstractComponent {
	/**
	 * Disable the tabbing of the host itself, only the internal button should be highlightable.
	 */
	@HostBinding('attr.tabindex') private readonly tabindex: number = -1;

	/**
	 * Whether or not the component should be 100% width.
	 *
	 * **Not applicable to the `Close` button variant.**
	 */
	@HostBinding('class.full-width') @Input() public fullWidth: boolean = false;

	/**
	 * The button classes enum exposed to the template.
	 */
	public readonly buttonClasses: typeof ButtonVariants = ButtonVariants;

	/**
	 * The tooltip of the button. This should not be the same as the text.
	 *
	 * This is required since WCAG AA-label dictates it.
	 */
	public title: StringSignal = input.required<string>();

	/**
	 * The styling to apply to the button.
	 *
	 * Default value is `Regular`
	 */
	public buttonType: Signal<ButtonVariantsType> = input<ButtonVariantsType>('Regular');

	/**
	 * An extra class to apply to the button.
	 */
	public customButtonClass: StringSignal = input<string>('');

	/**
	 * The class of the icon we wish to display
	 */
	public icon: StringSignal = input<string>();

	/**
	 * Whether or not the button should be disabled.
	 * If `true`, the button click event will be blocked,
	 * as well as possible navigation.
	 *
	 * Default value is `false`
	 */
	public isDisabled: BooleanSignal = input<boolean>(false);

	/**
	 * Provide a Cypress Tag to the button.
	 */
	public buttonCypressTag: Signal<CypressTagsPaths> = input<CypressTagsPaths>();

	/**
	 * Emits when the button has been pressed.
	 *
	 * This emitter should not be used to handle navigation. If internal navigation is
	 * required, use the `VlaioInternalButtonComponent`. If external navigation is required,
	 * use the `VlaioExternalButtonComponent`.
	 */
	public readonly handleClick: OutputEmitterRef<void> = output<void>();

	/**
	 * @param browserService Allows the handler to run code only when in the browser.
	 */
	constructor(public readonly browserService: BrowserService) {}

	/**
	 * If the button is not disabled, emit the click.
	 */
	protected handleButtonClick(): void {
		if (!this.isDisabled() && this.browserService.isBrowser) {
			this.handleClick.emit();
		}
	}
}
